import React, { useEffect, useState, createElement } from 'react';
import { ArrowRightIcon, ZapIcon, DatabaseIcon, LockIcon, ActivityIcon, BuildingIcon, ShoppingCartIcon, CodeIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
export function WebsiteHostingPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Website hosting | Snelle & veilige hosting | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'Betrouwbare website hosting voor WordPress. Snelle servers, SSL-certificaat, dagelijkse backups en 99.9% uptime. Hosting speciaal geoptimaliseerd voor jouw website.';
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': 'Website hosting | Snelle & veilige hosting | Noord',
      'og:description': 'Betrouwbare website hosting voor WordPress. Snelle servers, SSL-certificaat, dagelijkse backups en 99.9% uptime.',
      'og:type': 'website',
      'og:url': 'https://noorddigital.nl/website-hosting',
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const faqs = [{
    q: 'Is hosting verplicht voor een website?',
    a: 'Ja, elke website heeft hosting nodig om online te zijn. Of je nu een kleine bedrijfswebsite hebt of een uitgebreide webshop, zonder hosting is je website niet bereikbaar voor bezoekers. Wij regelen betrouwbare hosting zodat jij je geen zorgen hoeft te maken over techniek.'
  }, {
    q: 'Zit hosting inbegrepen bij een nieuwe website?',
    a: 'Ja, bij Noord Digital is hosting standaard inbegrepen wanneer je een website bij ons laat maken. Je krijgt snelle servers, dagelijkse backups, SSL-beveiliging en monitoring. Alles in één pakket zonder verborgen kosten.'
  }];
  return <main className="pt-16 lg:pt-[72px]" data-id="element-1962">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-1963">
        <div className="absolute inset-0 pointer-events-none" data-id="element-1964">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-1965" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-1966" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-1967" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1968">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm mb-8 animate-fade-in-up" data-id="element-1969">
            <a href="#/" className="text-navy-400 hover:text-white transition-colors" data-id="element-1970">
              Home
            </a>
            <span className="text-navy-500" data-id="element-1971">/</span>
            <a href="#/kennisbank" className="text-navy-400 hover:text-white transition-colors" data-id="element-1972">
              Kennisbank
            </a>
            <span className="text-navy-500" data-id="element-1973">/</span>
            <span className="text-accent-light font-medium" data-id="element-1974">
              Website hosting
            </span>
          </nav>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up" data-id="element-1975">
            Snelle en veilige website hosting
          </h1>
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto mb-10 animate-fade-in-up-delay-1" data-id="element-1976">
            Betrouwbare hosting voor professionele websites. Razendsnel, altijd
            beveiligd en volledig beheerd door ons.
          </p>
          <div className="animate-fade-in-up-delay-2" data-id="element-1977">
            <a href="#hosting-voordelen" className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-1978">
              Bekijk hosting
              <ArrowRightIcon className="w-5 h-5" data-id="element-1979" />
            </a>
          </div>
        </div>
      </section>

      {/* Hosting voordelen */}
      <section id="hosting-voordelen" className="py-24 bg-surface-muted" data-id="element-1980">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1981">
          <div className="text-center mb-16" data-id="element-1982">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1983">
              Waarom kiezen voor onze website hosting?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-1984">
              Onze hosting is geoptimaliseerd voor snelheid, veiligheid en
              betrouwbaarheid.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1985">
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-1986">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1987">
                <ZapIcon className="w-6 h-6 text-accent" data-id="element-1988" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1989">
                Snelle servers
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1990">
                Onze servers zijn geoptimaliseerd voor WordPress. Jouw website
                laadt razendsnel, wat zorgt voor een betere gebruikerservaring
                en hogere Google-ranking.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-1991">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1992">
                <DatabaseIcon className="w-6 h-6 text-accent" data-id="element-1993" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1994">
                Dagelijkse backups
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1995">
                Elke dag wordt er automatisch een volledige backup gemaakt van
                jouw website en database. Bij problemen herstellen we alles
                snel.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-1996">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1997">
                <LockIcon className="w-6 h-6 text-accent" data-id="element-1998" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1999">
                SSL-beveiliging
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2000">
                Gratis SSL-certificaat inbegrepen. Jouw website is beveiligd met
                HTTPS, wat vertrouwen wekt bij bezoekers en door Google wordt
                beloond.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-2001">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2002">
                <ActivityIcon className="w-6 h-6 text-accent" data-id="element-2003" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2004">
                24/7 Monitoring
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2005">
                Wij monitoren jouw website continu op uptime en prestaties. Bij
                problemen grijpen we direct in, vaak voordat jij het merkt.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Voor wie */}
      <section className="py-24 bg-white" data-id="element-2006">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2007">
          <div className="text-center mb-16" data-id="element-2008">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2009">
              Voor wie is onze hosting geschikt?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2010">
              Onze snelle website hosting is geschikt voor uiteenlopende
              websites en bedrijven.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2011">
            <div className="border border-navy-100 rounded-card p-8 text-center" data-id="element-2012">
              <div className="w-14 h-14 rounded-full bg-accent-light flex items-center justify-center mx-auto mb-6" data-id="element-2013">
                <BuildingIcon className="w-7 h-7 text-accent" data-id="element-2014" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2015">
                Bedrijven
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2016">
                Betrouwbare hosting voor bedrijfswebsites die altijd online en
                snel bereikbaar moeten zijn voor klanten.
              </p>
            </div>
            <div className="border border-navy-100 rounded-card p-8 text-center" data-id="element-2017">
              <div className="w-14 h-14 rounded-full bg-accent-light flex items-center justify-center mx-auto mb-6" data-id="element-2018">
                <ShoppingCartIcon className="w-7 h-7 text-accent" data-id="element-2019" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2020">Webshops</h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2021">
                Snelle en veilige hosting voor webshops waar betrouwbaarheid en
                laadsnelheid direct invloed hebben op de omzet.
              </p>
            </div>
            <div className="border border-navy-100 rounded-card p-8 text-center" data-id="element-2022">
              <div className="w-14 h-14 rounded-full bg-accent-light flex items-center justify-center mx-auto mb-6" data-id="element-2023">
                <CodeIcon className="w-7 h-7 text-accent" data-id="element-2024" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2025">
                WordPress websites
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2026">
                Hosting die speciaal is geoptimaliseerd voor WordPress, met de
                juiste serverinstellingen voor maximale prestaties.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gerelateerde pagina's */}
      <section className="py-16 bg-surface-muted border-y border-navy-100" data-id="element-2027">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2028">
          <h3 className="text-lg font-semibold text-navy-900 mb-6" data-id="element-2029">
            Gerelateerde pagina's:
          </h3>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-2030">
            <a href="#/hosting-onderhoud" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2031">
              Website onderhoud
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2032">•</span>
            <a href="#/hosting-onderhoud" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2033">
              Website beveiliging
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2034">•</span>
            <a href="#/wordpress-websites" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2035">
              WordPress websites
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white" data-id="element-2036">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2037">
          <div className="text-center mb-16" data-id="element-2038">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2039">
              Veelgestelde vragen over website hosting
            </h2>
          </div>

          <div className="space-y-4" data-id="element-2040">
            {faqs.map((faq, index) => <div key={index} className="bg-surface-muted rounded-xl shadow-sm border border-navy-50 overflow-hidden" data-id="element-2041">
                <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-2042">
                  <span className="font-bold text-navy-900" data-id="element-2043">{faq.q}</span>
                  {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-2044" /> : <ChevronDownIcon className="w-5 h-5 text-navy-400 flex-shrink-0" data-id="element-2045" />}
                </button>
                {openFaq === index && <div className="px-6 pb-5 text-slate-600 leading-relaxed" data-id="element-2046">
                    {faq.a}
                  </div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-2047" />
    </main>;
}